import { apiClient } from '@kss/api';

/**
 * Purchase ↔ Inventory ↔ Sales integration helper.
 *
 * Stock is moved by POSTing `/purchase/InventoryMovement` rows; the backend
 * derives `StockBalance.quantityOnHand` from them. The registry Purchase /
 * Sales / POS screens save documents + lines but historically never wrote a
 * movement, so stock never changed — this util closes that gap.
 *
 * Field shape verified against the live swagger (Entities.Purchase.InventoryMovement)
 * and mirrors the proven InvoiceIngest posting:
 *   productId, warehouseId, movementType, direction, quantity,
 *   unitOfMeasure, unitCost, occurredAt, sourceType, sourceId.
 */

export interface MovementLine {
  productId: number | string | null | undefined;
  quantity: number;
  unitCost?: number;
  unitOfMeasure?: string;
}

export interface PostMovementsOpts {
  /** 'In' = stock increases (purchase receipt); 'Out' = decreases (sale). */
  direction: 'In' | 'Out';
  /** Free-form movement type. Defaults: In→'Receipt', Out→'Issue'. */
  movementType?: string;
  /** Document kind that caused the movement, e.g. 'PurchaseInvoice', 'SalesInvoice', 'POS'. */
  sourceType: string;
  /** Numeric id of the source document (for traceability + reversal). */
  sourceId: number | string | null | undefined;
  /** Human doc number — only used to build a readable movement code. */
  docNumber: string;
  /** ISO timestamp the movement occurred. Defaults to now. */
  occurredAt?: string;
  /** Warehouse the stock moves in/out of. */
  warehouseId?: number | null;
  lines: MovementLine[];
}

/**
 * Post one InventoryMovement per real line. Best-effort: a movement failure
 * does not throw (the document itself is already saved), so a transient
 * inventory error never blocks billing. Returns the count attempted.
 */
export async function postStockMovements(opts: PostMovementsOpts): Promise<number> {
  const { direction, sourceType, sourceId, docNumber, lines } = opts;
  const movementType = opts.movementType ?? (direction === 'In' ? 'Receipt' : 'Issue');
  const occurredAt = opts.occurredAt ?? new Date().toISOString();
  const warehouseId = opts.warehouseId ?? null;
  const real = lines.filter((l) => l.productId != null && Number(l.quantity) > 0);
  if (!real.length) return 0;
  await Promise.all(
    real.map((l, i) =>
      apiClient.post('/purchase/InventoryMovement', {
        code: `IM-${docNumber}-${i + 1}`,
        name: `${sourceType} ${docNumber}`,
        status: 0,
        productId: Number(l.productId),
        warehouseId,
        movementType,
        direction,
        quantity: Number(l.quantity) || 0,
        unitOfMeasure: l.unitOfMeasure || 'PCS',
        unitCost: Number(l.unitCost ?? 0),
        occurredAt,
        sourceType,
        sourceId: sourceId != null ? Number(sourceId) : null,
      }).catch(() => undefined),
    ),
  );
  return real.length;
}

/** First warehouse id (fallback when a screen has no warehouse picker). */
export async function resolveDefaultWarehouseId(): Promise<number | null> {
  try {
    const r = await apiClient.get('/master-data/Warehouse');
    const d = r.data;
    const rows = (Array.isArray(d) ? d : (d?.items ?? d?.data ?? [])) as Array<Record<string, unknown>>;
    return rows[0]?.id != null ? Number(rows[0].id) : null;
  } catch {
    return null;
  }
}
